import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import {
  ArrowLeft,
  FileText,
  Clock,
  CheckCircle,
  Star,
  Award,
  Bell,
  ExternalLink,
  Calendar,
  Target,
  Edit,
  Eye,
  Trash2
} from 'lucide-react';

const MinhasAvaliacoes = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState({
    funcionario_nome: '',
    avaliacoes_pendentes: [],
    avaliacoes_finalizadas: [],
    autoavaliacoes_subordinados: [], // NOVO
    notificacoes: [],
    total_pendentes: 0,
    total_finalizadas: 0
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadMinhasAvaliacoes();
  }, []);

  const loadMinhasAvaliacoes = async () => {
    try {
      setLoading(true);
      
      // Buscar avaliações pendentes
      const pendentesResponse = await axios.get(`${backendUrl}/api/performance/evaluations/my-pending`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Buscar avaliações finalizadas
      const finalizadasResponse = await axios.get(`${backendUrl}/api/performance/evaluations/my-completed`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Buscar autoavaliações dos subordinados (apenas para gestores)
      let autoavaliacoesSubordinados = [];
      if (user?.id) {
        try {
          const autoavalResponse = await axios.get(`${backendUrl}/api/performance/evaluations/subordinate-self/${user.id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          autoavaliacoesSubordinados = autoavalResponse.data || [];
        } catch (error) {
          console.log('Usuário não é gestor ou não tem subordinados:', error);
        }
      }
      
      const pendentes = pendentesResponse.data || [];
      const finalizadas = finalizadasResponse.data || [];
      
      setDados({
        funcionario_nome: user?.name || 'Colaborador',
        avaliacoes_pendentes: pendentes,
        avaliacoes_finalizadas: finalizadas,
        autoavaliacoes_subordinados: autoavaliacoesSubordinados,
        notificacoes: [],
        total_pendentes: pendentes.length,
        total_finalizadas: finalizadas.length
      });
      
    } catch (error) {
      console.error('Erro ao carregar avaliações:', error);
      toast.error('Erro ao carregar suas avaliações');
    } finally {
      setLoading(false);
    }
  };

  const iniciarAvaliacao = async (item) => {
    try {
      // A avaliação já existe! Apenas navegar para preenchimento
      if (!item.id) {
        toast.error('ID da avaliação não encontrado');
        return;
      }
      
      toast.success('Abrindo formulário de avaliação...');
      
      // Navegar para a tela de preenchimento usando o ID existente
      navigate(`/preencher-avaliacao/${item.id}`);
      
    } catch (error) {
      console.error('Erro ao abrir avaliação:', error);
      toast.error('Erro ao abrir formulário de avaliação');
    }
  };

  const abrirAvaliacao = (link_acesso) => {
    if (link_acesso) {
      window.open(`${backendUrl}${link_acesso}`, '_blank');
    } else {
      toast({
        title: "Link Indisponível",
        description: "Entre em contato com o RH para obter o link de acesso",
        variant: "destructive"
      });
    }
  };

  const handleExcluir = async (evaluationId, nome) => {
    if (!window.confirm(`Tem certeza que deseja excluir a avaliação de "${nome}"?`)) return;
    try {
      await axios.delete(`${backendUrl}/api/performance/evaluations/${evaluationId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Avaliação excluída com sucesso');
      await loadMinhasAvaliacoes();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir avaliação');
    }
  };

  const formatarData = (dataString) => {
    if (!dataString) return 'N/A';
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadge = (score) => {
    if (score >= 8) return 'bg-green-100 text-green-800';
    if (score >= 6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">Carregando suas avaliações...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => window.history.back()}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Minhas Avaliações</h1>
              <p className="text-gray-600">Olá {dados.funcionario_nome}, aqui estão suas avaliações de desempenho</p>
            </div>
          </div>
        </div>

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pendentes</CardTitle>
              <Clock className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{dados.total_pendentes}</div>
              <p className="text-xs text-gray-600">Aguardando sua resposta</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Finalizadas</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{dados.total_finalizadas}</div>
              <p className="text-xs text-gray-600">Avaliações respondidas</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Notificações</CardTitle>
              <Bell className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{dados.notificacoes.length}</div>
              <p className="text-xs text-gray-600">Novas notificações</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs de Avaliações */}
        <Tabs defaultValue="pendentes" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="pendentes">
              Pendentes ({dados.total_pendentes})
            </TabsTrigger>
            <TabsTrigger value="finalizadas">
              Finalizadas ({dados.total_finalizadas})
            </TabsTrigger>
            <TabsTrigger value="autoavaliacoes-subordinados">
              Autoavaliações Subordinados
            </TabsTrigger>
            <TabsTrigger value="notificacoes">
              Notificações ({dados.notificacoes.length})
            </TabsTrigger>
          </TabsList>

          {/* Aba Pendentes */}
          <TabsContent value="pendentes">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-orange-600" />
                  Avaliações Pendentes
                </CardTitle>
                <CardDescription>
                  Avaliações de desempenho aguardando preenchimento
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dados.avaliacoes_pendentes.length === 0 ? (
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      Parabéns! Você não tem avaliações pendentes no momento.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-4">
                    {dados.avaliacoes_pendentes.map((item, index) => (
                      <Card key={index} className="border-l-4 border-l-orange-500">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-lg">
                                Avaliar: {item.employee?.nome || 'Colaborador'}
                              </h4>
                              <p className="text-gray-600 text-sm mb-2">
                                Ciclo: {item.cycle?.nome || 'Ciclo de Avaliação'}
                              </p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4" />
                                  Período: {item.cycle?.data_inicio ? new Date(item.cycle.data_inicio).toLocaleDateString('pt-BR') : 'N/A'} - {item.cycle?.data_fim ? new Date(item.cycle.data_fim).toLocaleDateString('pt-BR') : 'N/A'}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Target className="h-4 w-4" />
                                  Tipo: {
                                    item.evaluation_type === 'bifocal' ? 'Avaliação Bifocal' :
                                    item.evaluation_type === 'autoavaliacao' ? 'Autoavaliação' :
                                    item.evaluation_type === 'gestor' ? 'Avaliação do Gestor' :
                                    'Avaliação'
                                  }
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-orange-600 border-orange-600">
                                Pendente
                              </Badge>
                              {user?.is_admin && (
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleExcluir(item.id, item.employee?.nome)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                onClick={() => iniciarAvaliacao(item)}
                                className="bg-orange-600 hover:bg-orange-700"
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Preencher Avaliação
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aba Finalizadas */}
          <TabsContent value="finalizadas">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-green-600" />
                  Avaliações Finalizadas
                </CardTitle>
                <CardDescription>
                  Suas avaliações respondidas e resultados
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dados.avaliacoes_finalizadas.length === 0 ? (
                  <Alert>
                    <FileText className="h-4 w-4" />
                    <AlertDescription>
                      Você ainda não finalizou nenhuma avaliação.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-4">
                    {dados.avaliacoes_finalizadas.map((item, index) => (
                      <Card key={item.id || index} className="border-l-4 border-l-green-500">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-lg">
                                Avaliação de: {item.employee?.nome || 'Colaborador'}
                              </h4>
                              <p className="text-gray-600 text-sm mb-2">
                                Ciclo: {item.cycle?.nome || 'Ciclo de Avaliação'}
                              </p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4" />
                                  Finalizada em: {item.completed_at ? new Date(item.completed_at).toLocaleDateString('pt-BR') : 'N/A'}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Target className="h-4 w-4" />
                                  Tipo: {
                                    item.evaluation_type === 'bifocal' ? 'Avaliação Bifocal' :
                                    item.evaluation_type === 'autoavaliacao' ? 'Autoavaliação' :
                                    item.evaluation_type === 'gestor' ? 'Avaliação do Gestor' :
                                    'Avaliação'
                                  }
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className="text-green-600 border-green-600">
                                Finalizada
                              </Badge>
                              {user?.is_admin && (
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleExcluir(item.id, item.employee?.nome)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                              {/* Mostrar botões apenas se evaluator_type = "superior" */}
                              {item.evaluator_type === 'superior' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => navigate(`/preencher-avaliacao/${item.id}`)}
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    Ver Detalhes
                                  </Button>
                                  {/* Se for bifocal, mostrar botão de comparação */}
                                  {item.evaluation_type === 'bifocal' && item.parent_evaluation_id && (
                                    <Button
                                      size="sm"
                                      className="bg-purple-600 hover:bg-purple-700"
                                      onClick={() => navigate(`/comparacao-bifocal/${item.parent_evaluation_id}`)}
                                    >
                                      <Target className="h-4 w-4 mr-2" />
                                      Comparar
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aba Autoavaliações dos Subordinados */}
          <TabsContent value="autoavaliacoes-subordinados">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-purple-600" />
                  Autoavaliações dos Subordinados
                </CardTitle>
                <CardDescription>
                  Autoavaliações finalizadas pelos seus subordinados diretos (visualização apenas)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dados.autoavaliacoes_subordinados.length === 0 ? (
                  <Alert>
                    <FileText className="h-4 w-4" />
                    <AlertDescription>
                      Nenhuma autoavaliação de subordinados disponível.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-4">
                    {dados.autoavaliacoes_subordinados.map((item, index) => (
                      <Card key={item.id || index} className="border-l-4 border-l-purple-500">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-lg">
                                Colaborador: {item.employee?.nome || 'Nome não disponível'}
                              </h4>
                              <p className="text-gray-600 text-sm mb-2">
                                Ciclo: {item.cycle?.nome || 'Ciclo de Avaliação'}
                              </p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4" />
                                  Finalizada em: {item.completed_at ? new Date(item.completed_at).toLocaleDateString('pt-BR') : 'N/A'}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Target className="h-4 w-4" />
                                  Tipo: Autoavaliação
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className="text-purple-600 border-purple-600">
                                Autoavaliação
                              </Badge>
                              {user?.is_admin && (
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleExcluir(item.id, item.employee?.nome)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/preencher-avaliacao/${item.id}`)}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                Ver Autoavaliação
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aba Notificações */}
          <TabsContent value="notificacoes">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-blue-600" />
                  Notificações
                </CardTitle>
                <CardDescription>
                  Suas notificações recentes
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dados.notificacoes.length === 0 ? (
                  <Alert>
                    <Bell className="h-4 w-4" />
                    <AlertDescription>
                      Você não tem notificações no momento.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-4">
                    {dados.notificacoes.map((notif) => (
                      <Card key={notif.id} className="border-l-4 border-l-blue-500">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold">{notif.titulo}</h4>
                              <p className="text-gray-600 text-sm mb-2">{notif.mensagem}</p>
                              <span className="text-xs text-gray-500">
                                {formatarData(notif.criada_em)}
                              </span>
                            </div>
                            {notif.link_acesso && (
                              <Button
                                size="sm"
                                onClick={() => abrirAvaliacao(notif.link_acesso)}
                                variant="outline"
                              >
                                <Target className="h-4 w-4 mr-1" />
                                Acessar
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MinhasAvaliacoes;